/**
 * Wielki Format adapter — per-type import adapter for 'wielki format' media type.
 *
 * Includes siatka rows migrated to 'wielki format'.
 *
 * Required columns (in addition to COMMON_REQUIRED_COLUMNS):
 * - 'Material'
 * - 'Oswietlenie'
 *
 * Optional type-specific columns:
 * - 'Multiformat' (max 64 chars, design.md D-1)
 * - 'Szerokosc [m]'
 * - 'Wysokosc [m]'
 * - '[Mural] Produkcja eco muralu' (shared with murals on some formats)
 */
import type { MediaUnitImportAdapter, ValidationError } from './types.js';
import { toScalarValue, type MappedRow } from '../validate-row.js';
import {
  buildBaseRow,
  validateMaterialLighting,
  COMMON_REQUIRED_COLUMNS,
  COMMON_OPTIONAL_COLUMNS,
} from './parse-row.helper.js';

const REQUIRED_COLUMNS = [
  ...COMMON_REQUIRED_COLUMNS,
  'Typ nośnika',
  'Materiał',
  'Oświetlenie',
] as const;

const OPTIONAL_COLUMNS = [
  ...COMMON_OPTIONAL_COLUMNS,
  'Szerokość [m]',
  'Wysokość [m]',
  'Multiformat',
  '[Mural] Produkcja eco muralu',
] as const;

const MULTIFORMAT_MAX_LENGTH = 64;

export const wielkiFormatAdapter: MediaUnitImportAdapter = {
  mediaType: 'wielki format',
  requiredColumns: REQUIRED_COLUMNS,
  optionalColumns: OPTIONAL_COLUMNS,

  validate(row: MappedRow): ValidationError[] {
    const errors: ValidationError[] = validateMaterialLighting(row);

    // Validate Multiformat max length (design.md D-1: 64 chars)
    const multiformatScalar = toScalarValue(row['multiformat']);
    if (multiformatScalar && multiformatScalar.trim().length > MULTIFORMAT_MAX_LENGTH) {
      errors.push({
        field: 'Multiformat',
        message: `Multiformat nie może przekraczać ${MULTIFORMAT_MAX_LENGTH} znaków`,
        code: 'multiformat_too_long',
        params: { max: String(MULTIFORMAT_MAX_LENGTH) },
        severity: 'error',
      });
    }

    return errors;
  },

  parseRow(row: MappedRow) {
    return buildBaseRow(row, 'wielki format');
  },
};
